import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DateFnsAdapter } from '../../../datetime-input/custom-date-fns-adapter';
import { DynamicFormatDate } from '../../../datetime-input/dynamic-format-date';
import {
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

import { InlineDateComponent } from './date/inline-date.component';
import { InlineDateTimeComponent } from './datetime/inline-date-time.component';
import { InlineTimeComponent } from './time/inline-time.component';

@UntilDestroy()
@Component({
  selector: 'be-datetime-editor',
  templateUrl: './datetime-editor.component.html',
  styleUrls: ['./datetime-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DynamicFormatDate,
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useExisting: DynamicFormatDate,
    },
  ],
  standalone: false,
})
export class TableDateTimeEditorComponent implements AfterViewInit, OnInit {
  valueChange = output<string | null>();

  inlineDateComponent = viewChild<InlineDateComponent>('inlineDateComponent');
  inlineDateTimeComponent = viewChild<InlineDateTimeComponent>('inlineDateTimeComponent');
  inlineTimeComponent = viewChild<InlineTimeComponent>('inlineTimeComponent');

  @Input() config?: InlineEditorConfig;
  @Input() layoutMode?: InlineEditorLayoutMode;
  @Input() value: number | string | undefined;

  formControl!: FormControl<string | null | undefined>;

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  get dataType(): 'Date' | 'Time' | 'DateTime' {
    const dataType = this.config?.columnSchema?.dataType;
    this.assertDataType(dataType);
    return dataType;
  }

  get isDatePickerOpened(): boolean {
    return (
      this.inlineDateComponent()?.datepicker()?.opened ||
      this.inlineDateTimeComponent()?.isDatePickerOpened ||
      false
    );
  }

  ngOnInit(): void {
    this.formControl = this.createFormControl();
  }

  ngAfterViewInit(): void {
    if (!this.isInputLike) {
      setTimeout(() => {
        switch (this.dataType) {
          case 'Date':
            this.openDatePicker();
            break;
          case 'Time':
            this.openTimePicker();
            break;
        }
      });
    }
  }

  openDatePicker(): void {
    this.inlineDateComponent()?.openDatePicker();
  }
  openTimePicker(): void {
    this.inlineTimeComponent()?.openTimePicker();
  }

  clearDateTimeValue(): void {
    this.formControl.patchValue(null);
    this.value = undefined;
    this.emitValueChanged(null);
  }

  protected emitValueChanged(value: string | null): void {
    this.valueChange.emit(value);
  }

  private createFormControl(): FormControl<string | null | undefined> {
    const value = this.value;
    this.assertValueType(value);
    const formControl = new FormControl({
      value,
      disabled: false,
    });
    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.emitValueChanged(value ?? null);
    });
    return formControl;
  }

  private assertDataType(
    dataType: string | undefined,
  ): asserts dataType is 'Date' | 'Time' | 'DateTime' {
    if (dataType !== 'Date' && dataType !== 'Time' && dataType !== 'DateTime') {
      throw new TypeError(
        `Unsupported data type for TableDateTimeEditorComponent: ${dataType}. Supported types are 'Date', 'Time' and 'DateTime'.`,
      );
    }
  }

  private assertValueType(value: unknown): asserts value is string | null | undefined {
    if (typeof value !== 'string' && value !== null && value !== undefined) {
      throw new TypeError(
        `CellDateTimeComponent: value is expected to be string | null | undefined, got ${typeof value}`,
      );
    }
  }
}
